// Logging initialization needs to be the first thing
import '../config/initBrowserLogging';
import '../config/initLibraryLogging';
import '../config/legacyLogging';

import { useApolloClientOptions } from '@/apollo/apolloClient';
import { appVariables, Product } from '@/apollo/appVariables';
import { ApolloProvider, makeVar } from '@apollo/client';
import { AppProviders } from '@components/app/AppProviders';
import { SliErrorBoundary } from '@components/app/Error';
import {
  AppProps,
  NextPageWithLayout,
  AuthenticatedPageComponentProps,
  PublicPageComponentProps
} from '@components/app/types';
import { setThemeFromEventInfoTheme } from '@context/ThemeProvider';
import { ApolloClientFactory, useApollo } from '@cvent/apollo-client';
import { ErrorBoundary } from '@cvent/sli-nextjs-metrics';
import { appFeaturesVar } from '@hooks/useAppFeatures';
import { httpLogRequestIdVar } from '@hooks/useAppStats';
import MetricsProvider, { Persona } from '@metrics/client/MetricsProvider';
import { initializeRUM } from '@metrics/initRum';
import { persistCsrfTokenForUser } from '@tools/csrf';
import httpLogPageLoadId from '@tools/httpLogPageLoadId';
import getConfig from 'next/config';
import React, { useEffect } from 'react';
import { getConsentChoices } from '@context/CookieConsentProvider';
import { updateGlobalLoggerContext } from '../config/updateGlobalLoggerContext';

// Global CSS must be imported from _app/index.js
import '@cvent/carina/fonts/fonts.web.css';
import '@cvent/videojs-thumbnail-preview/dist/videojs-thumbnail-preview.css';
import 'normalize.css'; // css reset
import 'stream-chat-react/dist/css/index.css';
// TODO move to inline styles
import '@components/liveQA/base/AccordionContentOverrides.css';
import '@components/shared/KeyboardOnlyOutline.css';

// HUB-16325: Required to make Carousel WCAG compliant for Cards w/ focusable elements
import 'wicg-inert';

const { publicRuntimeConfig } = getConfig();

const clientVersion = makeVar<string>(String(publicRuntimeConfig.CLIENT_VERSION) || 'unknown');

export default function App({
  Component,
  pageProps
}: {
  pageProps: { appProps: AppProps };
  Component: NextPageWithLayout;
}): JSX.Element {
  const {
    appVersion,
    appFeatures = {},
    featureVersion,
    initialLocale,
    eventId,
    attendeeId,
    httpLogRequestId,
    accountMappingId,
    contactId,
    publicPage,
    isWebinar,
    isAutomationAccount,
    isGPCEnabled,
    eventLicenseTypeName,
    theme,
    csrfToken
  } = pageProps?.appProps || {};

  // Only initialize RUM if metrics are enabled and the app is not running at localhost
  if (publicRuntimeConfig.ENABLE_METRICS === 'true' && publicRuntimeConfig.IS_DEV !== 'true') {
    const consentChoices = getConsentChoices(eventId);
    // Datadog should track interactions only when attendee has consented to Performance
    const trackingConsent = consentChoices !== false ? consentChoices?.Performance : false;
    /**
     * We use our own dd initialization logic since the version of NX we are currently on, does not support
     * passing custom configuration to the initRum func exported by the framework.
     */
    initializeRUM(trackingConsent);
  }

  if (theme && theme.primary != null && theme.secondary != null && theme.background != null && theme.mood != null) {
    setThemeFromEventInfoTheme(theme);
  }

  updateGlobalLoggerContext({ accountMappingId, eventId, attendeeId, httpLogRequestId, httpLogPageLoadId });

  useEffect(() => {
    // Force logging in browser
    // eslint-disable-next-line no-console
    console.log(`App Version: ${clientVersion()}; Feature Version: ${featureVersion}`);
    // We only want this to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set httpLogPageLoadIdVar immediately
  const logVar = httpLogRequestIdVar();
  if (httpLogRequestId && logVar !== httpLogRequestId) {
    httpLogRequestIdVar(httpLogRequestId);
  }

  const apolloClientOptions = useApolloClientOptions({
    appFeatures,
    eventId,
    attendeeId
  });
  const apolloClient = useApollo(
    { __APOLLO_STATE__: pageProps.appProps?.__APOLLO_STATE__ },
    new ApolloClientFactory({
      initialState: pageProps.appProps?.__APOLLO_STATE__,
      graphBasePath: '',
      graphUri: '',
      loginUrl: ''
    }),
    apolloClientOptions
  );

  // Force reload application when client gets out of step with server
  if (typeof window !== 'undefined' && !Component.noReload && appVersion !== clientVersion()) {
    // eslint-disable-next-line no-console
    console.log('Reloading application due to version mismatch', { appVersion, clientVersion: clientVersion() });
    window.location.reload();
    return null;
  }

  if (attendeeId) {
    persistCsrfTokenForUser(attendeeId, csrfToken);
  }

  const pageComponentProps: AuthenticatedPageComponentProps | PublicPageComponentProps = {
    ...pageProps,
    environmentName: publicRuntimeConfig.ENVIRONMENT_NAME,
    appFeatures,
    httplogrequestid: httpLogRequestId,
    eventId
  };

  const Layout =
    Component.getLayout ?? (({ children }: React.PropsWithChildren<unknown>): JSX.Element => <>{children}</>);

  appVariables({
    isWebinar,
    isAutomationAccount,
    initialLocale,
    eventId,
    attendeeId,
    publicPage,
    accountMappingId,
    contactId,
    environmentName: publicRuntimeConfig.ENVIRONMENT_NAME,
    isGPCEnabled
  });
  appFeaturesVar(appFeatures);

  const ahBaseProps = {
    eventId,
    attendeeId,
    product: isWebinar ? Product.WEBINAR : Product.ATTENDEE_HUB,
    accountMappingId,
    eventLicenseTypeName
  };

  return (
    <MetricsProvider
      environment={publicRuntimeConfig.IS_DEV === 'true' ? 'dev' : publicRuntimeConfig.ENVIRONMENT_NAME}
      rudderstackWriteKey={publicRuntimeConfig.RUDDER_WRITE_KEY}
      rudderstackDataPlaneUrl={publicRuntimeConfig.RUDDER_DATA_PLANE_URL}
      userId={attendeeId}
      persona={Persona.Attendee}
      isAutomationAccount={isAutomationAccount}
      appBaseProps={ahBaseProps}
    >
      <ApolloProvider client={apolloClient}>
        <AppProviders locale={initialLocale} isAuthenticated={attendeeId != null}>
          <ErrorBoundary
            httpLogPageLoadId={httpLogPageLoadId}
            sliData={{
              httpLogRequestId
            }}
            errorComponent={SliErrorBoundary}
            delegateTracking
          >
            <Layout {...pageProps.appProps}>
              <Component {...pageComponentProps} />
            </Layout>
          </ErrorBoundary>
        </AppProviders>
      </ApolloProvider>
    </MetricsProvider>
  );
}
